import { Link } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';
import { counties } from '@/lib/locations/service-areas';
import { Button } from '@/components/ui/button';
import { useDocumentHead } from '@/hooks/use-document-head';

const cityPages = [
  { name: 'Altamonte Springs', slug: 'altamonte-springs', county: 'Seminole', zips: '32701, 32714' },
  { name: 'Apopka', slug: 'apopka', county: 'Orange', zips: '32703, 32712' },
  { name: 'Casselberry', slug: 'casselberry', county: 'Seminole', zips: '32707, 32730' },
  { name: 'Kissimmee', slug: 'kissimmee', county: 'Osceola', zips: '34741, 34744' },
  { name: 'Lake Mary', slug: 'lake-mary', county: 'Seminole', zips: '32746, 32795' },
  { name: 'Longwood', slug: 'longwood', county: 'Seminole', zips: '32750, 32779' },
  { name: 'Ocoee', slug: 'ocoee', county: 'Orange', zips: '34761' },
  { name: 'Orlando', slug: 'orlando', county: 'Orange', zips: '32801, 32803, 32808' },
  { name: 'Oviedo', slug: 'oviedo', county: 'Seminole', zips: '32765, 32766' },
  { name: 'Sanford', slug: 'sanford', county: 'Seminole', zips: '32771, 32773' },
  { name: 'Winter Garden', slug: 'winter-garden', county: 'Orange', zips: '34787' },
  { name: 'Winter Park', slug: 'winter-park', county: 'Orange', zips: '32789, 32792' },
];

export default function ServiceAreasPage() {
  useDocumentHead(
    'Cleaning Service Areas | Central Florida | SOTSVC',
    'Sonz of Thunder Services (SOTSVC) cleans across Orlando & Central Florida — Winter Park, Sanford, Oviedo, Kissimmee & more.',
  );
  return (
    <div className="min-h-screen pt-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Professional Cleaning Services Across Central Florida
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Sonz of Thunder Services proudly serves the greater Orlando metro area — residential, commercial, and deep cleaning across Orange, Seminole, and Osceola counties. We Bring the Boom to Every Room.
          </p>
        </div>

        {/* City Pages Grid */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Browse by City</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {cityPages.map((city) => (
              <Link
                key={city.slug}
                to={`/cleaning-services/${city.slug}-fl`}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all p-5 group border border-gray-100 hover:border-yellow-300"
              >
                <div className="flex items-start gap-3 mb-2">
                  <MapPin className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-yellow-600 transition-colors">
                      {city.name}, FL
                    </h3>
                    <p className="text-sm text-gray-500">{city.county} County</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mb-3">ZIP: {city.zips}</p>
                <span className="text-yellow-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                  View Services <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Counties Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {counties.map((county) => (
            <div
              key={county.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                {county.name}
              </h2>

              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Major Cities
                </h3>
                <ul className="space-y-1">
                  {county.majorCities.map((city) => (
                    <li key={city} className="text-gray-600">
                      {city}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Popular Services
                </h3>
                <ul className="space-y-1">
                  {county.popularServices.map((service) => (
                    <li key={service} className="text-gray-600 capitalize">
                      {service.replace('-', ' ')}
                    </li>
                  ))}
                </ul>
              </div>

              <Button variant="gold" className="w-full" asChild>
                <Link to="/contact">Check Availability</Link>
              </Button>
            </div>
          ))}
        </div>

        {/* Service Guarantee */}
        <div className="bg-white rounded-lg shadow-sm p-8 text-center mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Our Service Guarantee
          </h2>
          <p className="text-gray-600 mb-6">
            As a locally owned business, we take pride in serving Central Florida
            with exceptional cleaning services. Our team is familiar with the unique
            needs of each community we serve.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            sotsvc.com · trustedcleaningexpert.com
          </p>
          <Button variant="gold" size="lg" asChild>
            <Link to="/contact">Schedule a Cleaning</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}